/**
 * kitBranding — maps test kit brand names to brand colors for GuidedLogCard.
 * Pure, no I/O. Used by KitPicker to render brand-colored cards.
 */
import type { TestKit } from '@reefapp/shared';

export interface KitBranding {
  /** Short display name (e.g. "Hanna Instruments"). */
  displayBrand: string;
  /** Specific product name from the kit. */
  productName: string;
  /** Brand card background color (hex). */
  color: string;
  /** Foreground text color for contrast on `color`. */
  textColor: string;
}

/** Brand-name (lowercase) → card palette. */
const BRAND_PALETTE: Record<string, { color: string; textColor: string }> = {
  'api':               { color: '#1a56a0', textColor: '#fff' },
  'seachem':           { color: '#2d6e3e', textColor: '#fff' },
  'salifert':          { color: '#d97706', textColor: '#fff' },
  'hanna instruments': { color: '#1d4ed8', textColor: '#fff' },
  'red sea':           { color: '#b91c1c', textColor: '#fff' },
  'jbl':               { color: '#15803d', textColor: '#fff' },
  'sera':              { color: '#7c3aed', textColor: '#fff' },
  'tropic marin':      { color: '#0e7490', textColor: '#fff' },
  'nt labs':           { color: '#374151', textColor: '#fff' },
  'fauna marin':       { color: '#0f766e', textColor: '#fff' },
  'elos':              { color: '#9333ea', textColor: '#fff' },
  'nyos':              { color: '#c2410c', textColor: '#fff' },
};

const DEFAULT_PALETTE: { color: string; textColor: string } = {
  color: '#475569',
  textColor: '#fff',
};

/**
 * Derive branding from a TestKit. Looks up the brand palette by
 * brand name (case-insensitive). Falls back to a neutral slate color.
 */
export function getKitBranding(kit: TestKit): KitBranding {
  const palette =
    BRAND_PALETTE[kit.brand.toLowerCase()] ?? DEFAULT_PALETTE;
  return {
    displayBrand: kit.brand,
    productName: kit.name,
    color: palette.color,
    textColor: palette.textColor,
  };
}
